import { ArrowClockwise, WarningOctagon } from '@phosphor-icons/react'
import { Component, createContext, memo, Suspense, useContext, useEffect, useLayoutEffect, useRef, type ReactNode } from 'react'
import { UNSAFE_RouteContext, useLocation, useNavigate, useNavigationType, useRoutes, type RouteObject } from 'react-router-dom'
import { create } from 'zustand'
import { Button, EmptyState, PageSkeleton } from '@/design-system'
import { useT } from '@/i18n'
import { urlOfLocation, useTabs, type WorkspaceTab } from '@/stores/tabs'

/**
 * Workspace tabs, the keep-alive half.
 *
 * Every open tab renders its own copy of the shell's child routes with `useRoutes(routes, tab.loc)`. useRoutes
 * wraps the result in its own LocationContext, so inside a tab useLocation / useSearchParams / useParams / NavLink
 * all see that tab's URL, not the address bar. Only the front tab is visible; the others stay mounted with
 * `hidden` + `inert`, so filters, typed text, open drawers and scroll survive a switch.
 *
 * The router's location is always the front tab's URL (WorkspaceSync keeps the two in step), which keeps the
 * address bar, back/forward, breadcrumbs and the sidebar highlight working as before.
 */

/** Tabs whose page chunk is still downloading (the strip shows a live dot). */
export const useTabActivity = create<{ loading: Record<string, true>; set: (id: string, on: boolean) => void }>()((set) => ({
  loading: {},
  set: (id, on) =>
    set((s) => {
      if (Boolean(s.loading[id]) === on) return s
      const loading = { ...s.loading }
      if (on) loading[id] = true
      else delete loading[id]
      return { loading }
    }),
}))

const TabActiveContext = createContext(true)
/** False while the page is kept alive behind another tab (pause scanners, timers, sounds…). */
export const useIsTabActive = () => useContext(TabActiveContext)

const isModalOpen = () => Boolean(document.querySelector('[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]'))
const inTextField = (el: EventTarget | null) => el instanceof HTMLElement && (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName))

/**
 * Keeps the router and the tab store in step, both ways:
 * - the router moved (a page called navigate(), back/forward, a link inside a page) → the front tab takes the URL;
 * - the front tab changed (tab click, close, Ctrl+Tab…) → navigate to that tab's URL.
 * Plus the keyboard shortcuts.
 */
export function useWorkspaceSync() {
  const location = useLocation()
  const navType = useNavigationType()
  const navigate = useNavigate()
  const activeId = useTabs((s) => s.activeId)
  const locUrl = urlOfLocation(location)
  const locRef = useRef(locUrl)
  locRef.current = locUrl
  // a navigation we started for a tab switch; locations in between (a fast double switch) are not "the page moved"
  const pending = useRef<{ url: string; at: number } | null>(null)
  const first = useRef(true)

  useLayoutEffect(() => {
    const p = pending.current
    if (p) {
      if (p.url === locUrl) pending.current = null
      else if (Date.now() - p.at < 1500) return
      else pending.current = null
    }
    useTabs.getState().syncLocation(
      { pathname: location.pathname, search: location.search, hash: location.hash, state: location.state, key: location.key },
      { pop: navType === 'POP', initial: first.current },
    )
    first.current = false
  }, [location]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const s = useTabs.getState()
    const tab = s.tabs.find((t) => t.id === s.activeId)
    if (!tab || tab.url === locRef.current) return
    pending.current = { url: tab.url, at: Date.now() }
    navigate(tab.url, { state: tab.loc.state })
  }, [activeId, navigate])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (isModalOpen()) return // a dialog of the front page is open: its tab must stay in front
      const store = useTabs.getState()
      const key = e.key.toLowerCase()
      const mod = e.ctrlKey || e.metaKey
      if ((mod && key === 'tab') || (e.ctrlKey && (key === 'pagedown' || key === 'pageup'))) {
        e.preventDefault()
        store.cycle(e.shiftKey || key === 'pageup' ? -1 : 1)
      } else if ((mod && key === 'w' && !e.shiftKey) || (e.altKey && key === 'w')) {
        e.preventDefault() // honoured when installed as an app; a browser tab may keep Ctrl+W for itself (Alt+W always works)
        store.close(store.activeId)
      } else if (e.altKey && !mod && /^[1-9]$/.test(e.key) && !inTextField(e.target)) {
        e.preventDefault()
        const target = e.key === '9' ? store.tabs[store.tabs.length - 1] : store.tabs[Number(e.key) - 1]
        if (target) store.focus(target.id)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])
}

/** Tabs render their routes from the top: no parent match, so useRoutes has nothing to be nested under. */
const ROOT_ROUTE_CONTEXT = { outlet: null, matches: [], isDataRoute: false }
const NO_ROUTES: RouteObject[] = []

export function TabPanes() {
  const shell = useContext(UNSAFE_RouteContext)
  // the shell route's children (data routes with ids, exactly what the router matches for the address bar)
  const routes = (shell.matches[shell.matches.length - 1]?.route.children as RouteObject[] | undefined) ?? NO_ROUTES
  const tabs = useTabs((s) => s.tabs)
  const activeId = useTabs((s) => s.activeId)
  const active = tabs.find((t) => t.id === activeId)

  // restored tabs mount on first visit, not all at once after a reload
  const visited = useRef(new Set<string>())
  visited.current.add(activeId)
  for (const id of visited.current) if (!tabs.some((t) => t.id === id)) visited.current.delete(id)

  // one scroll container (#main) for every tab: remember each tab's position, put it back on return
  const scrolls = useRef(new Map<string, number>())
  const shown = useRef<{ id: string; pathname: string } | null>(null)
  useEffect(() => {
    const main = document.getElementById('main')
    if (!main) return
    const onScroll = () => shown.current && scrolls.current.set(shown.current.id, main.scrollTop)
    main.addEventListener('scroll', onScroll, { passive: true })
    return () => main.removeEventListener('scroll', onScroll)
  }, [])
  useLayoutEffect(() => {
    const main = document.getElementById('main')
    const prev = shown.current
    shown.current = active ? { id: active.id, pathname: active.loc.pathname } : null
    if (!main || !active) return
    if (prev?.id === active.id) {
      // same tab, new page: start at the top like before
      if (prev.pathname !== active.loc.pathname) {
        main.scrollTo({ top: 0 })
        scrolls.current.set(active.id, 0)
      }
      return
    }
    main.scrollTop = scrolls.current.get(active.id) ?? 0
    // maps and charts measured while hidden (0×0) re-measure now
    const raf = requestAnimationFrame(() => window.dispatchEvent(new Event('resize')))
    return () => cancelAnimationFrame(raf)
  }, [active?.id, active?.loc.pathname]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <UNSAFE_RouteContext.Provider value={ROOT_ROUTE_CONTEXT}>
      {tabs.map((t) => (visited.current.has(t.id) ? <TabPane key={t.id} tab={t} routes={routes} active={t.id === activeId} /> : null))}
    </UNSAFE_RouteContext.Provider>
  )
}

const TabPane = memo(function TabPane({ tab, routes, active }: { tab: WorkspaceTab; routes: RouteObject[]; active: boolean }) {
  const element = useRoutes(routes, tab.loc)
  // `hidden` → display:none (the page-enter animation replays when it is shown again); `inert` → no focus, no clicks
  const inert = active ? {} : ({ inert: '' } as Record<string, string>)
  return (
    <div hidden={!active} {...inert} data-tab-id={tab.id} className="h-full">
      <TabActiveContext.Provider value={active}>
        <TabErrorBoundary resetKey={tab.url}>
          <Suspense fallback={<PaneFallback id={tab.id} />}>
            <div key={tab.loc.pathname} className="page-enter relative h-full">
              {/* the portal's page entrance (owner, 2026-10-20): a red beam sweeps the page once, top to bottom */}
              <span className="page-beam" aria-hidden />
              {element}
            </div>
          </Suspense>
        </TabErrorBoundary>
      </TabActiveContext.Provider>
    </div>
  )
})

function PaneFallback({ id }: { id: string }) {
  const setLoading = useTabActivity((s) => s.set)
  useEffect(() => {
    setLoading(id, true)
    return () => setLoading(id, false)
  }, [id, setLoading])
  return <PageSkeleton />
}

/** A page that throws takes down its own tab only; the others and the shell keep working. */
/** Each crash once per page load: a screen re-rendering into the same error does not file it twenty times. */
const reportedCrashes = new Set<string>()
export function reportCrash(error: unknown, componentStack: string) {
  const message = error instanceof Error ? error.message : String(error)
  const page = `${window.location.pathname}${window.location.search}`
  const key = `${page}|${message}`
  if (reportedCrashes.has(key)) return
  reportedCrashes.add(key)
  const stack = error instanceof Error ? String(error.stack ?? '') : ''
  void fetch('/api/v1/ext/error-reports', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json', 'x-aliens-os': '1' },
    body: JSON.stringify({
      page: page.slice(0, 200), action: 'crash', awb: '', title: 'حدث خطأ غير متوقع في هذه الصفحة', reason: message.slice(0, 500), code: 'PAGE_CRASH', status: null, requestId: '',
      client: { hint: '', details: [], technical: [stack.slice(0, 1500), componentStack.slice(0, 1500)].filter(Boolean), copied: message, userAgent: navigator.userAgent },
    }),
  }).catch(() => undefined)
}

class TabErrorBoundary extends Component<{ resetKey: string; children: ReactNode }, { error: unknown }> {
  state = { error: null as unknown }
  static getDerivedStateFromError(error: unknown) {
    return { error }
  }
  componentDidUpdate(prev: { resetKey: string }) {
    if (prev.resetKey !== this.props.resetKey && this.state.error) this.setState({ error: null })
  }
  /*
   * A screen that crashes reports itself to بلاغات الأخطاء, with the message and where it broke: «حصل خطأ غير متوقع»
   * on a screen nobody reported left nothing to go on (owner, 2026-10-04: opening an account in إدارة العملاء).
   */
  componentDidCatch(error: unknown, info: { componentStack?: string | null }) {
    reportCrash(error, info.componentStack ?? '')
  }
  render() {
    if (this.state.error) return <TabError error={this.state.error} onRetry={() => this.setState({ error: null })} />
    return this.props.children
  }
}

function TabError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const { locale } = useT()
  const ar = locale === 'ar'
  const message = error instanceof Error ? error.message : String(error)
  return (
    <EmptyState
      icon={WarningOctagon}
      title={ar ? 'حدث خطأ غير متوقع في هذه الصفحة' : 'This page ran into an unexpected error'}
      description={<span className="block max-w-md font-mono text-xs" dir="ltr">{message}</span>}
      action={
        <div className="flex flex-wrap justify-center gap-2">
          <Button icon={ArrowClockwise} variant="primary" onClick={onRetry}>{ar ? 'إعادة المحاولة' : 'Try again'}</Button>
          <Button onClick={() => window.location.reload()}>{ar ? 'تحديث التطبيق' : 'Reload app'}</Button>
        </div>
      }
    />
  )
}
